// Records which camera recorded the selfie (PLAN-Device-Context.md §B4.2).
//
// Amplify's FaceLivenessDetectorCore opens the camera itself, so we wrap
// navigator.mediaDevices.getUserMedia once and read the label + settings of
// the video track it gets back. Virtual cameras (OBS, ManyCam…) show up here
// by name, which is the cheapest video-injection signal there is.
//
// Captured on every verification (always-on tier). Sent with /complete or
// /abort for that attempt; nothing is sent on its own.

export interface CameraSnapshot {
  label?: string;
  facingMode?: string;
  width?: number;
  height?: number;
  frameRate?: number;
  videoInputCount?: number;
  videoInputLabels?: string[];
}

let installed = false;
let current: CameraSnapshot = {};

async function enumerateCameras(): Promise<void> {
  try {
    // Labels are only populated once camera permission is granted, which is
    // the case right after getUserMedia resolves.
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((d) => d.kind === 'videoinput');
    current.videoInputCount = cameras.length;
    const labels = cameras.map((d) => d.label).filter(Boolean).slice(0, 10);
    if (labels.length) current.videoInputLabels = labels;
  } catch {
    // not available — leave it out
  }
}

export function installCameraProbe(): void {
  const media = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
  if (installed || !media?.getUserMedia) return;
  installed = true;

  const original = media.getUserMedia.bind(media);
  media.getUserMedia = async (constraints?: MediaStreamConstraints) => {
    const stream = await original(constraints);
    try {
      const track = stream.getVideoTracks()[0];
      if (track) {
        const s = track.getSettings();
        current = {
          ...current,
          label: track.label || undefined,
          facingMode: s.facingMode,
          width: s.width,
          height: s.height,
          frameRate: s.frameRate,
        };
        void enumerateCameras();
      }
    } catch {
      // never interfere with the liveness check
    }
    return stream;
  };
}

/** Forget the previous attempt's camera (each attempt reports its own). */
export function resetCameraProbe(): void {
  current = {};
}

export function cameraSnapshot(): CameraSnapshot | undefined {
  return Object.keys(current).length ? { ...current } : undefined;
}
